import React, { useContext } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { AuthContext } from '../auth/AuthContext';
import { clearStoredAuthToken } from '../../services/api';
import NotificationBell from './NotificationBell';
import LanguageSwitcher from '../common/LanguageSwitcher';
import { useI18n } from '../../i18n/I18nContext';
import CartIcon from '../marketplace/CartIcon';
import { FaInstagram, FaPinterest, FaLinkedin } from 'react-icons/fa';
import { FaBluesky } from 'react-icons/fa6';

const Shell = styled.div`
  display: grid;
  grid-template-rows: auto 1fr auto;
  min-height: 100vh;
`;

const Nav = styled.nav`
  position: sticky;
  top: 0;
  backdrop-filter: saturate(160%) blur(8px);
  background: rgba(10, 12, 20, 0.92);
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  z-index: 50;
`;

const NavInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 28px;
`;

const Brand = styled(Link)`
  font-weight: 800;
  letter-spacing: 0.4px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 20px;
  color: ${({ theme }) => theme.colors.text};
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    color: ${({ theme }) => theme.colors.accent};
  }
`;

const NavLogo = styled.img`
  height: 32px;
  width: auto;
  display: block;
  object-fit: contain;
`;

const NavActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-right: 16px;
`;

const GhostButton = styled(Link)`
  padding: 10px 14px;
  border-radius: ${({ theme }) => theme.radii.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: rgba(255,255,255,0.02);
  color: ${({ theme }) => theme.colors.text};
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255,255,255,0.05);
    border-color: ${({ theme }) => theme.colors.accent};
    transform: translateY(-1px);
  }
`;

const PrimaryButton = styled(Link)`
  padding: 10px 16px;
  border-radius: ${({ theme }) => theme.radii.sm};
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.accent});
  color: #0b0f1a;
  font-weight: 700;
  box-shadow: ${({ theme }) => theme.shadows.glowPrimary};
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.glowPrimary}, 0 8px 25px rgba(124,92,255,0.3);
  }
`;

const Main = styled.main`
  width: 100%;
`;

const Footer = styled.footer`
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.textDim};
  padding: 24px 28px;
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const FooterColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const FooterColumnTitle = styled.h3`
  font-size: 0.95rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 0.5rem 0;
`;

const FooterLink = styled(Link)`
  color: ${({ theme }) => theme.colors.textDim};
  text-decoration: none;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  
  &:hover {
    color: ${({ theme }) => theme.colors.accent};
  }
`;

const SocialMediaBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const SocialLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.radii.sm};
  background: ${({ theme }) => theme.colors.bgElevated};
  border: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.textDim};
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 18px;

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    color: white;
    border-color: ${({ theme }) => theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(124, 92, 255, 0.3);
  }
`;

const FooterBrand = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FooterLegalLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-top: 0.5rem;

  a {
    display: block;
  }
`;

const FooterLogo = styled.img`
  width: 40px;
  height: 40px;
  object-fit: contain;
  display: block;
`;

const FooterCopyright = styled.div`
  color: ${({ theme }) => theme.colors.textDim};
  font-size: 0.9rem;
  line-height: 1.5;
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 24px;
`;

const Layout = ({ children }) => {
  const { user } = useContext(AuthContext);
  const { t } = useI18n();

  return (
    <Shell>
      <Nav>
        <NavInner>
          <Brand to="/">
            <NavLogo src="/logo.jpg" alt="" aria-hidden="true" /> PolyAssetVault
          </Brand>
            <NavActions>
              <LanguageSwitcher />
              <NavLinks>
              <GhostButton to="/catalog">{t('nav.browse')}</GhostButton>
              <GhostButton to="/briefs">{t('nav.briefs')}</GhostButton>
              <GhostButton to="/creators">{t('nav.creators')}</GhostButton>
              {user && <GhostButton to="/feed">Feed</GhostButton>}
              <GhostButton to="/render-lab">Render Lab</GhostButton>
            </NavLinks>
            {user && <NotificationBell />}
            <CartIcon />
            {user ? (
              <>
                <GhostButton to="/dashboard">Dashboard</GhostButton>
                <PrimaryButton to="/" onClick={() => {
                  clearStoredAuthToken();
                  window.location.reload();
                }}>Logout</PrimaryButton>
              </>
            ) : (
              <>
                <GhostButton to="/login">Login</GhostButton>
                <PrimaryButton to="/register">Sign up</PrimaryButton>
              </>
            )}
          </NavActions>
        </NavInner>
      </Nav>
      <Main>
        <Container>{children}</Container>
      </Main>
      <Footer>
        <Container>
          <FooterContent>
            <FooterColumn>
              <FooterBrand>
                <FooterLogo src="/logo.jpg" alt="PolyAssetVault" />
                <FooterCopyright>
                  © {new Date().getFullYear()} PolyAssetVault — {t('footer.tagline')}
                </FooterCopyright>
                <FooterLegalLinks>
                  <FooterLink to="/privacy-policy">{t('legal.privacy')}</FooterLink>
                  <FooterLink to="/terms-of-use">{t('legal.terms')}</FooterLink>
                  <FooterLink to="/cookie-policy">{t('legal.cookies')}</FooterLink>
                  <FooterLink to="/copyright-policy">{t('legal.copyright')}</FooterLink>
                  <FooterLink to="/refund-policy">{t('legal.refund')}</FooterLink>
                </FooterLegalLinks>
              </FooterBrand>
            </FooterColumn>
            <FooterColumn>
              <FooterColumnTitle>Social Media</FooterColumnTitle>
              <SocialMediaBlock>
                <SocialLinks>
                  <SocialLink href="https://x.com/polyassetvault" target="_blank" rel="noopener noreferrer" title="Follow us on X">
                    𝕏
                  </SocialLink>
                  <SocialLink href="https://www.instagram.com/polyassetvault/" target="_blank" rel="noopener noreferrer" title="Follow us on Instagram">
                    <FaInstagram size={18} />
                  </SocialLink>
                  <SocialLink href="https://bsky.app/profile/polyassetvault.bsky.social" target="_blank" rel="noopener noreferrer" title="Follow us on Bluesky">
                    <FaBluesky size={18} />
                  </SocialLink>
                  <SocialLink href="https://youtube.com/@polyassetvault" target="_blank" rel="noopener noreferrer" title="Subscribe on YouTube">
                    📺
                  </SocialLink>
                  <SocialLink href="https://discord.gg/aTh56jtERw" target="_blank" rel="noopener noreferrer" title="Join our Discord">
                    💬
                  </SocialLink>
                </SocialLinks>
                <SocialLinks>
                  <SocialLink href="https://pinterest.com/PolyAssetVault/" target="_blank" rel="noopener noreferrer" title="Follow us on Pinterest">
                    <FaPinterest size={18} />
                  </SocialLink>
                  <SocialLink href="https://www.linkedin.com/showcase/polyassetvault/" target="_blank" rel="noopener noreferrer" title="PolyAssetVault on LinkedIn">
                    <FaLinkedin size={18} />
                  </SocialLink>
                </SocialLinks>
              </SocialMediaBlock>
            </FooterColumn>
            <FooterColumn>
              <FooterColumnTitle>Resources</FooterColumnTitle>
              <FooterLink to="/guide">Help</FooterLink>
              <FooterLink to="/privacy-settings">Privacy Settings</FooterLink>
              <FooterLink to="/accessibility">Accessibility</FooterLink>
              <FooterLink to="/report">Report Abuse</FooterLink>
            </FooterColumn>
            <FooterColumn>
              <FooterColumnTitle>Company</FooterColumnTitle>
              <FooterLink to="/about">About</FooterLink>
              <FooterLink to="/imprint">Imprint</FooterLink>
            </FooterColumn>
          </FooterContent>
        </Container>
      </Footer>
    </Shell>
  );
};

export default Layout;


